import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';
import { WebSocketState, LiveSignalData } from '../../types/websocket';

/**
 * A CellAlert as stored client-side: stamped with the browser ARRIVAL time.
 * Seen/unseen math must use received_ms, never the producer's time_stamp —
 * a plant-floor producer with a skewed clock would otherwise mint alerts
 * that arrive already "seen".
 */
export type StoredCellAlert = CellAlert & { received_ms: number };

export interface WebSocketSliceState {
  connections: {
    [facilityId: number]: WebSocketState;
  };
  liveSignalData: {
    [facilityId: number]: LiveSignalData;
  };
  cellAlerts: {
    [facilityId: number]: StoredCellAlert[];
  };
  globalError: string | null;
}

const initialState: WebSocketSliceState = {
  connections: {},
  liveSignalData: {},
  cellAlerts: {},
  globalError: null
};

const CELL_ALERTS_MAX = 200;

/**
 * Fold incoming alerts into the stored list (newest first).
 * - same alert_id replaces the existing entry (mute-duration reminders re-emit the
 *   same id — update in place, never stack duplicates)
 * - state==='cleared' also retires any open 'raised' entry matching source+code+cell
 *   (producers may mint a fresh alert_id for the clear)
 */
function foldCellAlerts(existing: StoredCellAlert[], incoming: StoredCellAlert[]): StoredCellAlert[] {
  let list = [...existing];
  incoming.forEach((alert) => {
    list = list.filter((a) => a.alert_id !== alert.alert_id);
    if (alert.state === 'cleared') {
      const matchIdx = list.findIndex(
        (a) =>
          a.state === 'raised' &&
          a.source === alert.source &&
          a.code === alert.code &&
          a.facility_line_id === alert.facility_line_id
      );
      if (matchIdx >= 0) {
        list[matchIdx] = { ...list[matchIdx], state: 'cleared', time_stamp: alert.time_stamp };
        return;
      }
    }
    list.unshift(alert);
  });
  return list.slice(0, CELL_ALERTS_MAX);
}

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    setConnectionState: (state, action: PayloadAction<{ facilityId: number; state: WebSocketState }>) => {
      const { facilityId, state: connectionState } = action.payload;
      if (connectionState.isConnected) {
        state.globalError = null;
      }
      state.connections[facilityId] = connectionState;
    },
    
    setLiveSignalData: (state, action: PayloadAction<{ facilityId: number; data: LiveSignalData }>) => {
      const { facilityId, data } = action.payload;
      state.liveSignalData[facilityId] = data;
    },
    
    clearLiveSignalData: (state, action: PayloadAction<number>) => {
      const facilityId = action.payload;
      delete state.liveSignalData[facilityId];
    },
    
    removeConnection: (state, action: PayloadAction<number>) => {
      const facilityId = action.payload;
      delete state.connections[facilityId];
      delete state.liveSignalData[facilityId];
    },
    
    addCellAlerts: (state, action: PayloadAction<{ facilityId: number; alerts: CellAlert[]; snapshot?: boolean }>) => {
      const { facilityId, alerts, snapshot } = action.payload;
      const receivedMs = Date.now();
      const stamped: StoredCellAlert[] = alerts.map((a) => ({ ...a, received_ms: receivedMs }));
      const existing = snapshot ? [] : (state.cellAlerts[facilityId] ?? []);
      state.cellAlerts[facilityId] = foldCellAlerts(existing, stamped);
    },

    clearCellAlerts: (state, action: PayloadAction<number>) => {
      delete state.cellAlerts[action.payload];
    },

    setGlobalError: (state, action: PayloadAction<string | null>) => {
      state.globalError = action.payload;
    },

    clearAllConnections: (state) => {
      state.connections = {};
      state.liveSignalData = {};
      state.cellAlerts = {};
      state.globalError = null;
    }
  }
});

export const {
  setConnectionState,
  setLiveSignalData,
  clearLiveSignalData,
  removeConnection,
  addCellAlerts,
  clearCellAlerts,
  setGlobalError,
  clearAllConnections
} = websocketSlice.actions;

export default websocketSlice.reducer; 